import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyOrders,
  cancelOrder,
  deleteMyOrder,
  clearMyOrders,
  downloadOrderInvoice,
  refreshOrderTracking,
  requestReturn,
  getCheckoutConfig
} from '../../services/orderService';
import { mediaUrl } from '../../utils/mediaUrl';
import '../../css/userOrders.css';
import '../../css/phase1.css';
import { useToast } from '../../contexts/ToastContext';
import formatPrice from '../../utils/formatPrice';

const STATUS_STEPS = [
  { key: 'Placed', id: 'placed', label: 'Placed' },
  { key: 'Processing', id: 'packed', label: 'Packed' },
  { key: 'Shipped', id: 'shipped', label: 'Shipped' },
  { key: 'Out for Delivery', id: 'ofd', label: 'Out for delivery' },
  { key: 'Delivered', id: 'delivered', label: 'Delivered' }
];

const STATUS_TONE = {
  Placed: 'placed',
  Processing: 'packed',
  Shipped: 'shipped',
  'Out for Delivery': 'ofd',
  Delivered: 'delivered',
  Cancelled: 'cancelled',
  Rejected: 'cancelled',
  Returned: 'returned'
};

const INVOICE_READY = ['Shipped', 'Out for Delivery', 'Delivered', 'Returned'];
const CANCELLABLE = ['Placed', 'Processing'];
const CANCEL_REASONS = [
  'Ordered by mistake',
  'Want to change size or colour',
  'Delivery is taking too long',
  'Found a better price',
  'Need to change address or payment',
  'Other'
];
const RETURN_REASONS = [
  'Size does not fit',
  'Received a damaged or defective item',
  'Received a different item',
  'Colour or fabric not as shown',
  'Quality not as expected',
  'Changed my mind'
];

const RETURN_STATUS_TEXT = {
  requested: 'Return requested. We will review it within 48 hours.',
  approved: 'Return approved. Our courier partner will contact you for pickup.',
  picked_up: 'Return picked up. We will check the item when it reaches us.',
  received: 'Returned item received. Your refund is being processed.',
  refunded: 'Return complete and refunded.',
  rejected: 'Return request not approved.'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// 🔹 Simple ETA logic
const getEstimatedDeliveryDate = (order) => {
  if (order.status === 'Delivered' && order.deliveredAt) {
    return new Date(order.deliveredAt);
  }
  // Courier's estimate when we have one
  if (order.shipment?.etd) {
    const etd = new Date(order.shipment.etd);
    if (!Number.isNaN(etd.getTime())) return etd;
  }

  let daysToAdd = 5;
  if (order.status === 'Processing') daysToAdd = 4;
  if (order.status === 'Shipped') daysToAdd = 2;
  if (order.status === 'Out for Delivery') daysToAdd = 0;

  const base = new Date(order.createdAt);
  base.setDate(base.getDate() + daysToAdd);
  return base;
};

const AccountOrders = () => {
  const { addToast, confirm } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // cancel state
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOther, setCancelOther] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // return state
  const [returnOrderId, setReturnOrderId] = useState(null);
  const [returnForm, setReturnForm] = useState({ reason: '', comments: '', upiId: '' });
  const [returning, setReturning] = useState(false);
  const [returnWindowDays, setReturnWindowDays] = useState(7);

  const [refreshingId, setRefreshingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!cancelOrderId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !cancelling) closeCancelForm();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [cancelOrderId, cancelling]);

  const loadOrders = async () => {
    try {
      const data = await getMyOrders();
      const sorted = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sorted);
      const live = sorted.filter(
        (order) =>
          order.shipment?.provider === 'delhivery' &&
          order.shipment?.awbCode &&
          !['Delivered', 'Cancelled', 'Rejected', 'Returned'].includes(order.status)
      );
      await Promise.all(
        live.map(async (order) => {
          try {
            const updated = await refreshOrderTracking(order._id);
            setOrders((prev) => prev.map((row) => (row._id === updated._id ? { ...row, ...updated } : row)));
          } catch (err) {
            console.error(err);
          }
        })
      );
    } catch (err) {
      console.error(err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    getCheckoutConfig()
      .then((cfg) => setReturnWindowDays(cfg?.returnWindowDays ?? 7))
      .catch(() => {});
  }, []);

  const replaceOrder = (updated) =>
    setOrders((prev) => prev.map((o) => (o._id === updated._id ? { ...o, ...updated } : o)));

  const canReturn = (order) => {
    if (!returnWindowDays || order.status !== 'Delivered') return false;
    if (order.returnRequest?.status && order.returnRequest.status !== 'none') return false;
    const delivered = new Date(order.deliveredAt || order.updatedAt).getTime();
    return Date.now() <= delivered + returnWindowDays * 24 * 60 * 60 * 1000;
  };

  const handleRefreshTracking = async (order) => {
    setRefreshingId(order._id);
    try {
      replaceOrder(await refreshOrderTracking(order._id));
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleInvoice = async (order) => {
    setDownloadingId(order._id);
    try {
      await downloadOrderInvoice(order);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const openReturnForm = (orderId) => {
    setReturnOrderId(orderId);
    setReturnForm({ reason: '', comments: '', upiId: '' });
  };

  const handleSubmitReturn = async (order) => {
    if (!returnForm.reason) {
      addToast('Please choose a reason for the return.', 'error');
      return;
    }
    setReturning(true);
    try {
      replaceOrder(await requestReturn(order._id, returnForm));
      setReturnOrderId(null);
    } catch (err) {
      addToast(err.response?.data?.message || 'Could not submit your return request', 'error');
    } finally {
      setReturning(false);
    }
  };

  const openCancelForm = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setCancelOther('');
  };

  const closeCancelForm = () => {
    setCancelOrderId(null);
    setCancelReason('');
    setCancelOther('');
  };

  const handleSubmitCancel = async (orderId) => {
    const reason = cancelReason === 'Other' ? cancelOther.trim() : cancelReason.trim();
    if (!cancelReason) {
      addToast('Please choose a reason for cancellation.', 'error');
      return;
    }
    if (cancelReason === 'Other' && !reason) {
      addToast('Please tell us why you are cancelling this order.', 'error');
      return;
    }
    setCancelling(true);
    try {
      const result = await cancelOrder(orderId, reason);
      if (result?.warning) addToast(result.warning, 'info');
      await loadOrders();
      closeCancelForm();
    } catch (err) {
      console.error(err);
      addToast(err.response?.data?.message || 'Failed to cancel order', 'error');
    } finally {
      setCancelling(false);
    }
  };

  // ✅ Delete a single order from history
  const handleDeleteOrder = async (orderId) => {
    const ok = await confirm({
      title: 'Remove this order?',
      message: 'This only hides it from your list. You can still contact us with the order ID.',
      confirmLabel: 'Remove'
    });
    if (!ok) return;
    try {
      await deleteMyOrder(orderId);
      setOrders((prev) => prev.filter((o) => o._id !== orderId));
    } catch (err) {
      console.error(err);
      addToast('Failed to remove this order', 'error');
    }
  };

  // ✅ Delete entire order history
  const handleClearHistory = async () => {
    const ok = await confirm({
      title: 'Clear order list?',
      message: 'Remove all orders from your list? You can still contact us about them using the order ID.',
      confirmLabel: 'Clear list'
    });
    if (!ok) return;
    try {
      await clearMyOrders();
      setOrders([]);
    } catch (err) {
      console.error(err);
      addToast('Failed to clear your order list', 'error');
    }
  };

  if (loading) {
    return <div className="user-section-loading">Loading orders…</div>;
  }

  if (error) {
    return <div className="user-empty-state">{error}</div>;
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="user-card-block">
        <div className="user-empty-state">
          <div className="no-order-wrapper">
            <div className="no-order-top-bg"></div>

            <div className="no-order-card">
              <img
                src="https://images.vexels.com/media/users/3/146451/isolated/preview/d03c9de18db226a823d5adb337782f89-open-box-with-package-signs.png"
                alt="No orders"
                className="no-order-img"
              />

              <h2>NO ORDER FOUND</h2>
              <p>Looks like you haven’t made your order yet</p>

              <button className="no-order-btn" onClick={() => navigate("/all-product")}>
                Browse Products
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cancellableOrders = orders.filter((order) => CANCELLABLE.includes(order.status));
  const cancelTarget = orders.find((order) => order._id === cancelOrderId);

  return (
    <div className="orders-list-wrapper">
      <div className="orders-list-header">
        <h2>My Orders</h2>
        <div className="orders-header-actions">
          {cancellableOrders.length === 1 && (
            <button
              type="button"
              className="order-cancel-btn"
              onClick={() => openCancelForm(cancellableOrders[0]._id)}
            >
              Cancel order
            </button>
          )}
          {orders.length > 0 && (
            <button
              type="button"
              className="order-history-delete-btn"
              onClick={handleClearHistory}
            >
              Clear list
            </button>
          )}
        </div>
      </div>

      {orders.map((order) => {
        const currentStepIndex = STATUS_STEPS.findIndex(
          (s) => s.key === order.status
        );
        const isCancelled = ['Cancelled', 'Rejected'].includes(order.status);
        const isReturned = order.status === 'Returned';
        const shipment = order.shipment || {};
        const rr = order.returnRequest || {};
        const refund = order.refund || {};
        const etaDate = getEstimatedDeliveryDate(order);
        const items = order.orderItems || [];
        const firstItem = items[0];
        const statusTone = STATUS_TONE[order.status] || 'placed';
        const showRefund = refund.status && refund.status !== 'none' && (isCancelled || isReturned);

        return (
          <div key={order._id} className="order-card">
            <div className="order-card-header">
              <div>
                <div className="order-id-row">
                  <span className="order-id">{order.orderId || order._id}</span>
                  <span className={`order-status-pill is-${statusTone}`}>{order.status}</span>
                </div>
                <div className="order-date">Placed on {formatDate(order.createdAt)}</div>
                {order.paymentMethod === 'COD' && !order.isPaid && !isCancelled && (
                  <div className="order-pay-note">Pay on delivery</div>
                )}
              </div>
              <div className="order-summary-right">
                <div className="order-total">{formatPrice(order.totalPrice)}</div>
                {cancellableOrders.length > 1 && CANCELLABLE.includes(order.status) && (
                  <button
                    type="button"
                    className="order-cancel-btn"
                    onClick={() => openCancelForm(order._id)}
                  >
                    Cancel order
                  </button>
                )}
                {INVOICE_READY.includes(order.status) && (
                  <button
                    type="button"
                    onClick={() => handleInvoice(order)}
                    className="btn-order-invoice"
                    disabled={downloadingId === order._id}
                  >
                    {downloadingId === order._id ? 'Preparing…' : 'Download invoice'}
                  </button>
                )}
              </div>
            </div>

            {/* ✅ Product items preview */}
            {firstItem && (
              <div className="order-items-preview">
                <div className="order-item-main">
                  <div className="order-item-image-wrap">
                    <img
                      src={mediaUrl(
                        firstItem.image ||
                        firstItem.product?.images?.[0] ||
                        ''
                      )}
                      alt={firstItem.name || firstItem.product?.name || 'Product'}
                      className="order-item-image"
                    />
                  </div>
                  <div className="order-item-info">
                    <div className="order-item-name">
                      {firstItem.name || firstItem.product?.name}
                    </div>
                    <div className="order-item-meta">
                      {firstItem.size && <span>Size: {firstItem.size}</span>}
                      {firstItem.color && <span>Color: {firstItem.color}</span>}
                    </div>
                    <div className="order-item-qty">
                      Qty: {firstItem.qty}
                    </div>
                  </div>
                </div>

                {items.length > 1 && (
                  <div className="order-item-more">
                    + {items.length - 1} more item
                    {items.length - 1 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}

            {/* 🔹 Timeline / Stepper */}
            {!isCancelled && !isReturned && (
              <div className="order-eta">
                {order.status === 'Delivered' && order.deliveredAt
                  ? `Delivered on ${formatDate(order.deliveredAt)}`
                  : `Estimated delivery ${formatDate(etaDate.toISOString())}`}
              </div>
            )}

            <div className={`order-tracker ${isCancelled || isReturned ? 'cancelled' : ''}`}>
              {STATUS_STEPS.map((step, idx) => {
                const isCompleted = currentStepIndex >= 0 && idx < currentStepIndex;
                const isCurrent = idx === currentStepIndex;
                return (
                  <div
                    key={step.key}
                    className={`order-step step-${step.id} ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                  >
                    <div className="step-circle">{isCompleted || isCurrent ? '✓' : idx + 1}</div>
                    <div className="step-label">{step.label}</div>
                    {step.key === 'Placed' && (
                      <div className="step-meta">{formatDate(order.createdAt)}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 🔹 Courier tracking */}
            {!isCancelled && (shipment.awbCode || ['Shipped', 'Out for Delivery', 'Processing'].includes(order.status)) && (
              <div className="order-ship-box">
                <div className="order-ship-head">
                  <div>
                    <strong>{shipment.courierName || 'Delhivery'}</strong>
                    {shipment.awbCode
                      ? <> · AWB <span className="order-awb">{shipment.awbCode}</span></>
                      : <span className="order-ship-wait"> · Tracking number after pickup</span>}
                    {shipment.lastStatus && shipment.awbCode && (
                      <div className="order-ship-status">{shipment.lastStatus}</div>
                    )}
                  </div>
                  <div className="order-ship-actions">
                    {shipment.trackingUrl && shipment.awbCode && (
                      <a href={shipment.trackingUrl} target="_blank" rel="noreferrer">Track parcel</a>
                    )}
                    {['shiprocket', 'delhivery'].includes(shipment.provider) && shipment.awbCode && order.status !== 'Delivered' && (
                      <button
                        type="button"
                        className="order-refresh-btn"
                        onClick={() => handleRefreshTracking(order)}
                        disabled={refreshingId === order._id}
                      >
                        {refreshingId === order._id ? 'Updating…' : 'Refresh'}
                      </button>
                    )}
                  </div>
                </div>
                {shipment.activities?.length > 0 && (
                  <ul className="order-activity">
                    {shipment.activities.slice(0, 4).map((a, i) => (
                      <li key={i}>
                        <span>{a.activity || a.status}{a.location ? ` — ${a.location}` : ''}</span>
                        {a.date && <time>{a.date}</time>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* 🔹 Return & refund status */}
            {rr.status && rr.status !== 'none' && (
              <div className={`order-note ${rr.status === 'rejected' ? 'is-bad' : 'is-info'}`}>
                {RETURN_STATUS_TEXT[rr.status] || `Return: ${rr.status}`}
                {rr.status === 'rejected' && rr.adminNote ? ` Reason: ${rr.adminNote}` : ''}
              </div>
            )}
            {showRefund && (
              <div className={`order-note ${refund.status === 'failed' ? 'is-bad' : 'is-good'}`}>
                {refund.status === 'processed' && `Refund of ${formatPrice(refund.amount)} issued${refund.reference ? ` (ref ${refund.reference})` : ''}. It can take 5–7 working days.`}
                {refund.status === 'pending' && `Refund of ${formatPrice(refund.amount)} started. It can take 5–7 working days.`}
                {refund.status === 'manual' && `Refund of ${formatPrice(refund.amount)} will be sent to your UPI ID shortly.`}
                {refund.status === 'failed' && 'Your refund needs attention. Our team will contact you.'}
              </div>
            )}

            {returnOrderId === order._id && (
              <div className="p1-form">
                <label>
                  Why are you returning this?
                  <select
                    value={returnForm.reason}
                    onChange={(e) => setReturnForm((f) => ({ ...f, reason: e.target.value }))}
                  >
                    <option value="">Choose a reason</option>
                    {RETURN_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Anything else we should know? (optional)
                  <textarea
                    rows={3}
                    value={returnForm.comments}
                    onChange={(e) => setReturnForm((f) => ({ ...f, comments: e.target.value }))}
                  />
                </label>
                {order.paymentMethod === 'COD' && (
                  <label>
                    UPI ID for your refund
                    <input
                      placeholder="name@bank"
                      value={returnForm.upiId}
                      onChange={(e) => setReturnForm((f) => ({ ...f, upiId: e.target.value.trim() }))}
                    />
                  </label>
                )}
                <div className="p1-form-actions">
                  <button type="button" className="p1-btn light" onClick={() => setReturnOrderId(null)} disabled={returning}>
                    Close
                  </button>
                  <button type="button" className="p1-btn" onClick={() => handleSubmitReturn(order)} disabled={returning}>
                    {returning ? 'Submitting…' : 'Request return'}
                  </button>
                </div>
              </div>
            )}

            {/* 🔹 Cancel info & action + delete button */}
            <div className="order-footer">
              {isCancelled && order.cancelReason && (
                <div className="order-cancel-reason">
                  <strong>{order.status}:</strong> {order.cancelReason}
                </div>
              )}

              <div className="order-footer-actions">
                {canReturn(order) && returnOrderId !== order._id && (
                  <button type="button" className="cancel-btn-link" onClick={() => openReturnForm(order._id)}>
                    Return item
                  </button>
                )}

                <button
                  type="button"
                  className="order-delete-btn"
                  onClick={() => handleDeleteOrder(order._id)}
                >
                  Remove from list
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {cancelTarget && (
        <div
          className="order-cancel-backdrop"
          role="presentation"
          onClick={() => !cancelling && closeCancelForm()}
        >
          <div
            className="order-cancel-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-order-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cancel-order-title">Cancel this order?</h3>
            <p>
              {cancelTarget.orderId || 'This order'} will be cancelled. If a courier booking
              exists, we will try to stop it too.
            </p>
            <label className="order-cancel-field">
              Reason for cancellation
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                disabled={cancelling}
              >
                <option value="">Select a reason</option>
                {CANCEL_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </label>
            {cancelReason === 'Other' && (
              <label className="order-cancel-field">
                Tell us more
                <textarea
                  rows={3}
                  value={cancelOther}
                  onChange={(e) => setCancelOther(e.target.value)}
                  placeholder="Why are you cancelling this order?"
                  disabled={cancelling}
                />
              </label>
            )}
            <div className="order-cancel-modal-actions">
              <button
                type="button"
                className="order-history-delete-btn"
                onClick={closeCancelForm}
                disabled={cancelling}
              >
                Keep order
              </button>
              <button
                type="button"
                className="order-cancel-btn is-solid"
                onClick={() => handleSubmitCancel(cancelTarget._id)}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOrders;
